/**
 * DashboardPage — replaces RunCtrl (~900 lines) + templates/dashboard.html.
 *
 * Controller → Hook/Component mapping:
 * ─────────────────────────────────────────────────────────────────────────────
 *  $scope.runsheet / calcTotals()      → useRunsheetContext()
 *  $scope.datalists                    → useDataLists()
 *  $scope.reload(force, reload)        → fetchRunsheet(force, reload)
 *  $scope.loadCustomer(item, idx)      → setCurrentLine / setCurrent (useRunsheetContext())
 *  $scope.toggleCurrent(status)        → handleToggle()
 *  $scope.actionToggle(status)         → handleActionToggle()
 *  $scope.collectFrame(status, cf)     → handleCollectFrame()
 *  $scope.checkEnvelope(status)        → handleCheckEnvelope()
 *  $scope.checkNotes()                 → checkNotes() (called after nav)
 *  $scope.numBagsUpdated(opt)          → handleBagsChange()
 *  $scope.updateOrder(item)            → handleOrderChange()
 *  $scope.markCompleted()              → handleMarkCompleted()
 *  $scope.showConfirmComplete()        → confirmComplete()
 *  $scope.editNotes/saveNotes          → NotesModal open/save
 *  $scope.resetWeight()                → handleResetWeight()
 *  $scope.exitTransferStation()        → ExitStationModal
 *  bgGeo.onGeofence (app.js)           → openExitStationForGeofence()
 *  $scope.resetWeight()                → handleResetWeight()
 *  $scope.smsClients / phoneClients()  → SmsModal / PhoneModal
 *  $scope.chatWindow()                 → navigate('/chat')
 *  $rootScope.$on('ReloadRunsheet')    → useSocketEvent('ReloadRunsheet') *
 *  $rootScope.$on('ExtraReceived')     → useSocketEvent('ExtraReceived')  *
 *  $rootScope.$on('MessageReceived')   → useSocketEvent('MessageReceived')*
 *  setInterval(reload, 3600000)        → useEffect + setInterval
 *  $interval(updateLocation, 5000)     → inside MapModal component
 *
 * * Socket events are wired in SocketContext and forwarded here through the
 *   raw `socket` ref via useSocketEvent().
 *
 * The runsheet, the selected stop, the stations and tracking come from RunsheetContext.
 * This page still triggers every fetch, starts tracking and registers the geofence exit
 * handler.
 *
 * AngularJS $ionicPopup.confirm/alert → modals.openConfirmModal() / modals.open()
 *   from @mantine/modals  (ModalsProvider must wrap the app in App.tsx).
 *
 * The size class only rearranges the page. Split puts the runsheet beside the customer
 * detail and Single-pane stacks them, inside one component tree, so the selected stop,
 * an open modal and unsaved input survive when a phone rotates.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Menu,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconCash,
  IconCheck,
  IconCircleCheck,
  IconClock,
  IconDotsVertical,
  IconFileText,
  IconLogout,
  IconMap,
  IconMessage,
  IconPackage,
  IconPhone,
  IconRefresh,
  IconDeviceMobileMessage,
  IconTransfer,
  IconTruck,
} from '@tabler/icons-react';
import axios from 'axios';

import { useAuth } from '@/contexts/AuthContext';
import { useRunsheetContext } from '@/contexts/RunsheetContext';
import { useSocketEvent } from '@/contexts/SocketContext';
import { useDataLists } from '@/hooks/useDataLists';
import { useMoveToOrder } from '@/hooks/useMoveToOrder';
import { useSizeClass } from '@/hooks/useSizeClass';
import { RunsheetList } from '@/components/RunsheetList';
import { CustomerDetail } from '@/components/CustomerDetail';
import { NotesModal } from '@/components/NotesModal';
import { ExitStationModal } from '@/components/ExitStationModal';
import { SmsModal } from '@/components/SmsModal';
import { PhoneModal } from '@/components/PhoneModal';
import { CustomerInfoModal } from '@/components/CustomerInfoModal';
import { ReasonModal } from '@/components/ReasonModal';
import { TermsModal } from '@/components/TermsModal';
import { BASE_URL } from '@/env';
import type { Icon } from '@tabler/icons-react';
import type { BagOption, ExtraPickup, Message, RunsheetLine, Transfer } from '@/types';

// Generate 0, 0.5, 1 … 100 bag options (mirrors original loop)
const BAG_OPTIONS: BagOption[] = [{ id: 0, label: 'No Pickup' }];
for (let i = 1; i <= 100; i++) {
  BAG_OPTIONS.push({ id: i, label: `${i} Bag${i > 1 ? 's' : ''}` });
  BAG_OPTIONS.push({ id: i + 0.5, label: `${i + 0.5} Bags` });
}

// Split frames each pane. Single-pane has only a divider between the stacked panes.
const SPLIT_PANE_FRAME = { border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 };
const STACK_DIVIDER = '3px solid var(--mantine-color-gray-4)';

/** What the driver answers at the collect-frame and cash prompts while completing a stop. */
type CompletionAnswers = Pick<RunsheetLine, 'no_pickup_reason' | 'picked_up_cash'>;

function playMessageSound(): void {
  new Audio('/mp3/message_ding.mp3').play().catch(() => undefined);
}

export default function DashboardPage() {
  const { logout, driver } = useAuth();
  const navigate = useNavigate();

  const {
    runsheet,
    loading,
    fetchRunsheet,
    saveRunsheet,
    updateLine,
    setRunsheet,
    stations,
    fetchStations,
    startTracking,
    onGeofenceExit,
    currentLine,
    setCurrentLine,
    current,
    setCurrent,
  } = useRunsheetContext();
  const { orderOptions, moveToOrder } = useMoveToOrder();
  const { datalists, fetchDataLists } = useDataLists();
  const { sizeClass, shortHeight } = useSizeClass();
  const split = sizeClass === 'split';

  const [bagsLoaded, setBagsLoaded] = useState(0);
  const [disableCountdown, setDisableCountdown] = useState(0);

  // Modal open states
  const [notesOpen, setNotesOpen] = useState(false);
  const [exitStationOpen, setExitStationOpen] = useState(false);
  const [exitStationId, setExitStationId] = useState<number | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [customerInfoOpen, setCustomerInfoOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Mutable refs used by callbacks that close over state
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    void fetchRunsheet(true);
    void fetchDataLists();

    // Start background geo + geofence setup
    if (driver) {
      fetchStations(true).then(stationList => {
        void startTracking(window.localStorage.getItem('26F2IkCfPo2u') ?? '', stationList);
      });
    }

    // Auto-reload every 60 minutes
    const timer = setInterval(() => void fetchRunsheet(true), 3_600_000);
    return () => {
      clearInterval(timer);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After runsheet loads, auto-select first pending line
  useEffect(() => {
    if (!runsheet?.lines) return;

    const firstPending = runsheet.lines.find(
      l => !l.driver_locked && l.num_bags > 0 && !l.pickupcode.match(/overdue|cancelled/i),
    );
    if (firstPending && !currentLine) {
      const idx = runsheet.lines.indexOf(firstPending);
      loadCustomer(firstPending, idx, true);
      if (idx > 5) scrollToLine(firstPending.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsheet?.id]);

  // ── Geofence exit ───────────────────────────────────────────────────────────
  // Leaving a transfer station opens ExitStationModal with that station preselected
  const openExitStationForGeofence = useCallback(
    (stationId: number) => {
      void fetchStations(false).then(() => {
        setExitStationId(stationId);
        setExitStationOpen(true);
      });
    },
    [fetchStations],
  );

  useEffect(() => {
    onGeofenceExit(openExitStationForGeofence);
    return () => onGeofenceExit(null);
  }, [onGeofenceExit, openExitStationForGeofence]);

  // ── Socket event handlers ───────────────────────────────────────────────────

  useSocketEvent<{ to_id: string; reload?: boolean }>('reload', data => {
    if (data.to_id === `driver${driver?.id ?? ''}` && data.reload) {
      void fetchRunsheet(true);
    }
  });

  useSocketEvent<Message & { reload?: boolean }>('driver', data => {
    if (data.to_id !== `driver${driver?.id ?? ''}`) return;
    if (data.reload) {
      void fetchRunsheet(true);
    } else {
      // Message received while on dashboard — show confirm popup (mirrors original)
      playMessageSound();
      modals.openConfirmModal({
        title: 'Message Received from Office',
        children: <Text>{data.message}</Text>,
        labels: { confirm: 'Go to Chat', cancel: 'Close' },
        onConfirm: () => navigate('/chat'),
      });
    }
  });

  useSocketEvent<ExtraPickup>('extra', data => {
    playMessageSound();

    const messages: Record<ExtraPickup['pickuptype'], string> = {
      extra_pickup: `Extra Pickup: ${data.address}, ${data.suburb}`,
      install_frame: `INSTALL FRAME & SACKS: ${data.num_bags} bags — ${data.address}, ${data.suburb}`,
      install_bin: `INSTALL WHEELIE BINS: ${data.num_bags} bins — ${data.address}, ${data.suburb}`,
    };
    const titles: Record<ExtraPickup['pickuptype'], string> = {
      extra_pickup: 'Extra Pickup Confirmation',
      install_frame: 'INSTALL FRAME and SACKS Confirmation',
      install_bin: 'INSTALL BINS Confirmation',
    };

    modals.openConfirmModal({
      title: titles[data.pickuptype],
      children: <Text>{messages[data.pickuptype]}</Text>,
      labels: { confirm: 'ACCEPT', cancel: 'DECLINE' },
      confirmProps: { color: 'green' },
      cancelProps: { color: 'red' },
      onConfirm: () =>
        axios
          .post(`${BASE_URL}extra`, { pickupid: data.pickupid, answer: true })
          .then(() => void fetchRunsheet(true))
          .catch(() => undefined),
      onCancel: () =>
        axios
          .post(`${BASE_URL}extra`, { pickupid: data.pickupid, answer: false })
          .catch(() => undefined),
    });
  });

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const scrollToLine = (id: number) => {
    setTimeout(() => {
      document
        .getElementById(`item${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  const loadCustomer = useCallback(
    (item: RunsheetLine, _index: number, suppress = false) => {
      setCurrentLine(item.id);
      setCurrent(item);
      if (!suppress) checkNextNotes(item);
    },
    [setCurrent, setCurrentLine],
  );

  const checkNextNotes = (item: RunsheetLine) => {
    const notes = `${item.cont_notes ?? ''} ${item.cust_notes ?? ''}`;
    const confirms: string[] = [];
    if (notes.match(/collect/i)) confirms.push('COLLECT FRAME');
    if (notes.match(/leave extra sack/i)) confirms.push('LEAVE EXTRA SACK');
    if (notes.match(/knock/i)) confirms.push('KNOCK ON DOOR');
    if (notes.match(/install/i)) confirms.push('INSTALL SACK & FRAME');

    if (confirms.length > 0) {
      modals.open({
        title: 'NEXT Pickup Information',
        children: (
          <Text component="div">
            The next pickup has some requirements:
            <ul>
              {confirms.map(c => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </Text>
        ),
      });
    }
  };

  // ── Toggle complete/pending — chains: toggleCurrent → collectFrame → checkEnvelope → actionToggle ──

  const handleActionToggle = useCallback(
    (status: boolean, answers: CompletionAnswers = {}) => {
      if (!current || !runsheet) return;

      const updatedLines = runsheet.lines.map(l => {
        if (l.id !== current.id) return l;
        return {
          ...l,
          driver_locked: status,
          // The answers go on the saved line, because the office reads them from the
          // PUT. Both are set on every toggle, so Mark Pending clears them and the next
          // completion saves only its own answers.
          no_pickup_reason: answers.no_pickup_reason,
          picked_up_cash: answers.picked_up_cash,
          lastmodified: Math.floor(Date.now() / 1000),
          dirty: true,
        };
      });

      const updatedRunsheet = { ...runsheet, lines: updatedLines };

      if (status) {
        setBagsLoaded(prev => prev + current.num_bags);
        setDisableCountdown(30);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setDisableCountdown(prev => {
            if (prev <= 1) {
              if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setBagsLoaded(prev => Math.max(0, prev - current.num_bags));
      }

      // Find next pending line
      const currentIdx = updatedLines.findIndex(l => l.id === current.id);
      let nextLine: RunsheetLine | null = null;
      if (status) {
        for (let i = currentIdx + 1; i < updatedLines.length; i++) {
          const l = updatedLines[i]!;
          if (!l.driver_locked && l.num_bags > 0 && !l.pickupcode.match(/overdue|cancelled/i)) {
            nextLine = l;
            break;
          }
        }
      }

      void saveRunsheet(updatedRunsheet).then(saved => {
        setRunsheet(saved);
        // Sync current in local state too
        const updatedCurrent = saved.lines.find(l => l.id === current.id);
        if (updatedCurrent) setCurrent(updatedCurrent);

        if (nextLine) {
          setCurrent(nextLine);
          setCurrentLine(nextLine.id);
          scrollToLine(nextLine.id);
          checkNextNotes(nextLine);
        } else if (status && saved.totals.pending === 0) {
          confirmComplete(saved);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, runsheet, saveRunsheet, setRunsheet],
  );

  const handleCheckEnvelope = useCallback(
    (status: boolean, answers: CompletionAnswers = {}) => {
      if (!current) return;
      if (current.envelope) {
        modals.openConfirmModal({
          title: 'Confirm Cash Pickup',
          children: <Text>This is an envelope customer, did you pick up any cash?</Text>,
          labels: { confirm: 'YES I HAVE', cancel: 'NO I Did Not' },
          onConfirm: () => handleActionToggle(status, { ...answers, picked_up_cash: true }),
          onCancel: () => handleActionToggle(status, { ...answers, picked_up_cash: false }),
        });
      } else {
        handleActionToggle(status, answers);
      }
    },
    [current, handleActionToggle],
  );

  const handleCollectFrame = useCallback(
    (status: boolean, collectFrame: boolean) => {
      if (!collectFrame) {
        handleCheckEnvelope(status);
        return;
      }
      modals.openConfirmModal({
        title: 'Confirm Collect Frame',
        children: <Text>Did you collect the frame from this address?</Text>,
        labels: { confirm: 'YES', cancel: 'NO' },
        onConfirm: () => handleCheckEnvelope(status),
        onCancel: () => setReasonOpen(true),
      });
    },
    [handleCheckEnvelope],
  );

  const handleReasonConfirm = (reason: string) => {
    setReasonOpen(false);
    // Only completing a stop asks about the frame, so the status is always true here.
    handleCheckEnvelope(true, { no_pickup_reason: reason });
  };

  const handleToggle = useCallback(
    (status: boolean) => {
      if (!current) return;

      if (!status) {
        handleActionToggle(false);
        return;
      }

      const notes = `${current.cont_notes ?? ''} ${current.cust_notes ?? ''}`;
      const collectFrame = notes.match(/collect/i) !== null;
      const confirms: string[] = [];
      if (notes.match(/leave extra sack/i)) confirms.push('LEAVE EXTRA SACK');
      if (notes.match(/knock/i)) confirms.push('KNOCK ON DOOR');
      if (current.pickupcode.match(/install/i)) {
        confirms.push(
          `${current.pickupcode} — ${current.num_bags} (${current.pickupcode.match(/frame/i) ? 'Sacks' : 'Bins'})`,
        );
      }

      if (confirms.length > 0) {
        modals.openConfirmModal({
          title: 'Confirm Actions',
          children: (
            <Text component="div">
              Please confirm you have done the following before marking complete:
              <ul>
                {confirms.map(c => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </Text>
          ),
          labels: { confirm: 'YES I HAVE', cancel: 'NO I Have Not' },
          onConfirm: () => handleCollectFrame(status, collectFrame),
        });
      } else {
        handleCollectFrame(status, collectFrame);
      }
    },
    [current, handleActionToggle, handleCollectFrame],
  );

  // ── Bag count ───────────────────────────────────────────────────────────────
  const handleBagsChange = useCallback(
    (num: number) => {
      if (!current || !runsheet) return;
      const updatedCurrent = { ...current, num_bags: num, dirty: true };
      setCurrent(updatedCurrent);
      void updateLine(updatedCurrent);
    },
    [current, runsheet, updateLine, setCurrent],
  );

  // ── Order change ────────────────────────────────────────────────────────────
  const handleOrderChange = useCallback(
    (newDisplayorder: number) => {
      if (!current) return;
      moveToOrder(current, newDisplayorder);
    },
    [current, moveToOrder],
  );

  // ── Pickup code change ──────────────────────────────────────────────────────
  const handlePickupCodeChange = useCallback(
    (code: string) => {
      if (!current) return;
      const updated = { ...current, pickupcode: code, dirty: true };
      setCurrent(updated);
      void updateLine(updated);
    },
    [current, updateLine, setCurrent],
  );

  // ── Mark run complete ────────────────────────────────────────────────────────
  const confirmComplete = useCallback(
    (rs = runsheet) => {
      if (!rs) return;
      modals.openConfirmModal({
        title: 'Close Run Sheet',
        children: (
          <Text>All pickups are marked complete. Do you want to close this Run Sheet now?</Text>
        ),
        labels: { confirm: 'Yes, Close Run', cancel: 'Not Yet' },
        confirmProps: { color: 'green' },
        onConfirm: () => {
          const updated = { ...rs, driver_status: 'completed' as const, dirty: true };
          void saveRunsheet(updated).then(() => {
            setCurrent(null);
            setCurrentLine(null);
            void fetchRunsheet(true);
          });
        },
      });
    },
    [runsheet, saveRunsheet, fetchRunsheet, setCurrent, setCurrentLine],
  );

  const handleMarkCompleted = useCallback(() => {
    if (!runsheet?.lines) return;
    const firstPending = runsheet.lines.find(
      l => !l.driver_locked && l.num_bags > 0 && !l.pickupcode.match(/overdue|cancelled/i),
    );
    if (firstPending) {
      alert('Cannot close run, there are incomplete pickups.');
      scrollToLine(firstPending.id);
    } else {
      confirmComplete();
    }
  }, [runsheet, confirmComplete]);

  // ── Reset bags loaded ────────────────────────────────────────────────────────
  const handleResetWeight = () => {
    modals.openConfirmModal({
      title: 'Confirm Reset',
      children: (
        <Text>
          Are you sure you want to manually reset the bags loaded? Bags loaded are automatically
          reset when exiting a Transfer Station.
        </Text>
      ),
      labels: { confirm: 'Yes', cancel: 'No' },
      onConfirm: () => setBagsLoaded(0),
    });
  };

  // ── Notes ────────────────────────────────────────────────────────────────────
  const handleSaveNotes = () => {
    if (!current) return;
    const updated = { ...current, dirty: true };
    void updateLine(updated);
    setNotesOpen(false);
  };

  // ── SMS / Phone lists ────────────────────────────────────────────────────────
  const smslist =
    runsheet?.lines.filter(
      l =>
        !l.driver_locked &&
        l.num_bags > 0 &&
        l.text_night_before &&
        l.ph_mob &&
        !l.pickupcode.match(/overdue|cancelled/i),
    ) ?? [];

  const phonelist =
    runsheet?.lines.filter(l => !l.driver_locked && l.num_bags > 0 && l.phone_night_before) ?? [];

  // ── Transfer station save ─────────────────────────────────────────────────────
  const handleSaveTransfer = async (transfer: Transfer) => {
    const { data } = await axios.post<{ success: boolean; message?: string }>(
      `${BASE_URL}transfer`,
      transfer,
    );
    if (!data.success) throw new Error(data.message ?? 'Save failed');
  };

  // ── Pickup code options for CustomerDetail Select ────────────────────────────
  // Handled inside CustomerDetail via datalists prop

  const title = !runsheet?.loaded
    ? 'Loading Runsheet Lines …'
    : runsheet.empty
      ? 'NO ACTIVE RUNSHEET'
      : driver
        ? `${runsheet.runnamewithdate} · ${driver.firstname} ${driver.lastname}`
        : runsheet.runnamewithdate;

  const canCloseRun = !!runsheet && !runsheet.empty && runsheet.totals.pending === 0;
  // Short height tightens the spacing around the Top bar and nothing else.
  const chromeSpacing = shortHeight ? 4 : 'xs';

  return (
    <Box h="100%" p={chromeSpacing} style={{ display: 'flex', flexDirection: 'column' }}>
      <LoadingOverlay visible={loading} />

      {/* ── Top bar ── */}
      <Group
        data-testid="top-bar"
        justify="space-between"
        mb={chromeSpacing}
        wrap="nowrap"
        gap="xs"
      >
        {/* Single-pane has no room for the run title, so it heads the More menu there. */}
        {split && (
          <Title
            order={5}
            flex={1}
            miw={0}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {title}
          </Title>
        )}

        {/* Totals. The badges aren't tap targets, so Single-pane packs them closer than 8px. */}
        <Group gap={split ? 'sm' : 4} wrap="nowrap" style={{ flexShrink: 0 }}>
          <StatBadge
            label="Bags"
            value={runsheet?.totals.bags ?? 0}
            icon={IconPackage}
            compact={!split}
          />
          <StatBadge label="Loaded" value={bagsLoaded} icon={IconTruck} compact={!split} />
          <StatBadge
            label="Done"
            value={runsheet?.totals.complete ?? 0}
            color="green"
            icon={IconCircleCheck}
            compact={!split}
          />
          {/* With nothing pending, the slot offers Close Run to a driver who answered
              "Not Yet" to the automatic prompt. */}
          {canCloseRun ? (
            <TopBarButton
              label="Close Run"
              color="green"
              icon={IconCheck}
              compact={!split}
              onClick={handleMarkCompleted}
            />
          ) : (
            <StatBadge
              label="Pending"
              value={runsheet?.totals.pending ?? 0}
              color="orange"
              icon={IconClock}
              compact={!split}
            />
          )}
        </Group>

        {/* Action buttons */}
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <TopBarButton
            label="Exit Station"
            color="red"
            icon={IconTransfer}
            compact={!split}
            onClick={() => {
              setExitStationId(null);
              setExitStationOpen(true);
            }}
          />
          {/* Office Chat is icon-only in both size classes, so Run map fits beside it in
              Split. Single-pane has no room for it with three-digit totals, so phones in
              portrait open the map from the More menu. */}
          <TopBarButton
            label="Office Chat"
            color="teal"
            icon={IconMessage}
            compact
            onClick={() => navigate('/chat')}
          />
          {split && (
            <TopBarButton
              label="Run map"
              color="gray"
              icon={IconMap}
              compact
              onClick={() => navigate('/map')}
            />
          )}

          {/* ── More menu ── */}
          <Menu
            position="bottom-end"
            // A phone in landscape is shorter than the menu, so the menu stops short of
            // the screen edge and scrolls.
            middlewares={{ size: { padding: 8 } }}
            // A menu item is a tap target, so it meets the Touch floor.
            styles={{ item: { minHeight: 44, fontSize: 'var(--mantine-font-size-md)' } }}
          >
            <Menu.Target>
              <ActionIcon variant="default" aria-label="More">
                <IconDotsVertical size={20} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown style={{ overflowY: 'auto' }}>
              {!split && <Menu.Label fz="sm">{title}</Menu.Label>}
              <Menu.Item leftSection={<IconMap size={18} />} onClick={() => navigate('/map')}>
                Run map
              </Menu.Item>
              <Menu.Item
                leftSection={<IconDeviceMobileMessage size={18} />}
                onClick={() =>
                  smslist.length > 0
                    ? setSmsOpen(true)
                    : notifications.show({ message: 'No clients for SMS' })
                }
              >
                SMS
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPhone size={18} />}
                onClick={() =>
                  phonelist.length > 0
                    ? setPhoneOpen(true)
                    : notifications.show({ message: 'No clients to phone' })
                }
              >
                Phone
              </Menu.Item>
              <Menu.Item
                leftSection={<IconRefresh size={18} />}
                onClick={() => void fetchRunsheet(true, true)}
              >
                Update
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCash size={18} />}
                disabled={bagsLoaded === 0}
                onClick={handleResetWeight}
              >
                Reset Bags
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCheck size={18} />}
                disabled={!canCloseRun}
                onClick={handleMarkCompleted}
              >
                Close Run
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileText size={18} />}
                onClick={() => setTermsOpen(true)}
              >
                Terms
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={18} />}
                onClick={() => {
                  logout();
                  navigate('/login', { replace: true });
                }}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* ── Main content ── */}
      <div
        style={{
          display: 'flex',
          flexDirection: split ? 'row' : 'column',
          flex: 1,
          minHeight: 0,
          gap: split ? 8 : 0,
        }}
      >
        {/* Runsheet: the top 38% in Single-pane, half the width in Split and 60% from 1200px */}
        <Box
          data-testid="runsheet-pane"
          flex={split ? { base: '1 1 0', lg: '3 1 0' } : '0 0 38%'}
          miw={0}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            ...(split ? SPLIT_PANE_FRAME : { borderBottom: STACK_DIVIDER }),
          }}
        >
          {runsheet?.loaded && !runsheet.empty ? (
            <RunsheetList
              lines={runsheet.lines}
              currentLineId={currentLine}
              onSelect={(item, idx) => loadCustomer(item, idx)}
            />
          ) : (
            <Box p="md">
              <Text c="dimmed">
                {runsheet?.loaded
                  ? 'No Active Runsheet. Ask office to push the next scheduled runsheet.'
                  : 'Loading…'}
              </Text>
            </Box>
          )}
        </Box>

        {/* Customer detail: the rest of the space */}
        <Box
          data-testid="detail-pane"
          flex={{ base: '1 1 0', lg: '2 1 0' }}
          miw={0}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            ...(split ? SPLIT_PANE_FRAME : undefined),
          }}
        >
          {current && (
            <CustomerDetail
              current={current}
              bagOptions={BAG_OPTIONS}
              datalists={datalists}
              disableCountdown={disableCountdown}
              onToggle={handleToggle}
              onBagsChange={handleBagsChange}
              onOrderChange={handleOrderChange}
              onPickupCodeChange={handlePickupCodeChange}
              onEditNotes={() => setNotesOpen(true)}
              onShowCustomerInfo={() => setCustomerInfoOpen(true)}
              orderOptions={orderOptions}
            />
          )}
        </Box>
      </div>

      {/* ── Modals ── */}
      <NotesModal
        opened={notesOpen}
        notes={current?.driver_notes ?? ''}
        onNotesChange={val => setCurrent(c => (c ? { ...c, driver_notes: val } : c))}
        onSave={handleSaveNotes}
        onClose={() => setNotesOpen(false)}
      />

      <ExitStationModal
        opened={exitStationOpen}
        stations={stations}
        initialStationId={exitStationId}
        onSave={handleSaveTransfer}
        onClose={() => setExitStationOpen(false)}
      />

      <SmsModal opened={smsOpen} smslist={smslist} onClose={() => setSmsOpen(false)} />

      <PhoneModal opened={phoneOpen} phonelist={phonelist} onClose={() => setPhoneOpen(false)} />

      {current && (
        <CustomerInfoModal
          opened={customerInfoOpen}
          current={current}
          onClose={() => setCustomerInfoOpen(false)}
        />
      )}

      <ReasonModal
        opened={reasonOpen}
        onConfirm={handleReasonConfirm}
        onClose={() => setReasonOpen(false)}
      />

      <TermsModal opened={termsOpen} onClose={() => setTermsOpen(false)} />
    </Box>
  );
}

// ── Small sub-components ──────────────────────────────────────────────────────

/** One total: a label over its badge in Split, an icon and the number in Single-pane. */
function StatBadge({
  label,
  value,
  icon: TotalIcon,
  color = 'blue',
  compact,
}: {
  label: string;
  value: number;
  icon: Icon;
  color?: string;
  compact: boolean;
}) {
  // Single-pane trims the padding, icon gap, text size and letter-spacing so four
  // three-digit totals fit beside the actions at 402px.
  if (compact) {
    return (
      <Badge
        role="img"
        aria-label={`${label}: ${value}`}
        color={color}
        size="lg"
        px={4}
        fz="xs"
        lts={0}
        styles={{ section: { marginInlineEnd: rem(2) } }}
        leftSection={<TotalIcon size={12} />}
      >
        {value}
      </Badge>
    );
  }
  return (
    <Box ta="center">
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
      <Badge color={color} size="lg">
        {value}
      </Badge>
    </Box>
  );
}

/** A Top bar action: icon and text in Split, icon-only with an `aria-label` in Single-pane. */
function TopBarButton({
  label,
  icon: ButtonIcon,
  color,
  compact,
  onClick,
}: {
  label: string;
  icon: Icon;
  color: string;
  compact: boolean;
  onClick: () => void;
}) {
  if (compact) {
    return (
      <ActionIcon color={color} aria-label={label} onClick={onClick}>
        <ButtonIcon size={20} />
      </ActionIcon>
    );
  }
  // Mantine has no 44px Button size, so the Touch floor is set as a height.
  return (
    <Button
      h={44}
      px="md"
      fz="md"
      color={color}
      leftSection={<ButtonIcon size={18} />}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
